export const FEATURE_FLAGS = {
  sessionRecap: "session-recap",
  supervisorMetrics: "supervisor-metrics",
  rdHome: "rd-home",
  sessionPatientLinks: "session-patient-links",
  socialNotes: "social-notes",
  // NO-475: arms the manual Enhance path (one model call, one SOAP Draft) for
  // one RD at a time. Default OFF so a deploy never starts generating against
  // live Patient Transcripts before the NO-366 canary says so.
  soapEnhancement: "soap-enhancement",
  // NO-772: streams content-free Enhance progress so the workspace shows
  // "Writing" and settles without waiting for the next poll. Default OFF;
  // the stream route answers 404 and the workspace keeps polling.
  soapProgressStream: "soap-progress-stream",
  // NO-775: lets an RD paste a transcript (and an optional prior SOAP note)
  // when Healthie has none, and generate the draft from it. Default OFF; the
  // Paste transcript action stays hidden and the route answers 501. Checked
  // together with soap-enhancement, which still gates every generation.
  manualSoapGeneration: "manual-soap-generation",
  // NO-785: lets an RD Re-enhance a never-approved SOAP Draft from the
  // workspace footer. Default OFF; the Re-enhance button stays hidden and the
  // route answers 501. Checked together with soap-enhancement.
  soapReEnhance: "soap-re-enhance",
  // NO-428: lets the worker start an Enhancement for the owning RD after two
  // minutes away from the Appointment, with the ready-copy hint and the "Draft
  // ready" mark. Default OFF; checked together with soap-enhancement, and a
  // missing flag or an evaluation error reads as off.
  soapAutoEnhance: "soap-auto-enhance",
  // NO-445: the Patient Quick Note on owned Appointments. Default OFF; the
  // routes answer 404 until the flag is on for the signed-in RD.
  patientQuickNote: "patient-quick-note",
  // NO-750: copies each saved Patient Quick Note into the Patient's Healthie
  // quick notes as one AlwaysAI-managed block. Default OFF, checked for the RD
  // who saves; turning it off stops every new Healthie write.
  patientQuickNoteHealthieSync: "patient-quick-note-healthie-sync",
  // NO-784: brings an edit made in Healthie inside AlwaysAI's block back as
  // Revisions, and shows Healthie's other quick notes text read-only under the
  // Quick Note. Default OFF, checked for the RD like the sync flag; an import
  // also needs patient-quick-note-healthie-sync on for that RD.
  patientQuickNoteHealthieImport: "patient-quick-note-healthie-import",
  // NO-754: the single-rail Always AI shell. Default OFF; target it only at
  // people who also have rd-home, or an RD-only viewer keeps the legacy chrome.
  alwaysBrand: "always-brand",
  // NO-830: the RD's in-app notification list in the single-rail shell.
  // Default OFF; the rail shows no Notifications row, /notifications is the
  // 404 page and /api/notifications* answer 404. NO-848: the Client
  // notification settings API, /api/patients/:patientId/notification-settings,
  // also answers 404 while it is off. NO-834: the account menu's Notifications
  // item and /settings/notifications (the 404 page while off), and
  // /api/notifications/settings/* (404 while off). Targeting is human-owned.
  notifications: "notifications",
  // Patient Detail Project flag (NO-835, NO-846, NO-847, NO-852): the rail's
  // Patients list, the patient page, its red-flag banner and its Quick facts
  // and Goals cards. Default OFF; while off the rail keeps its "Soon" Patients
  // item, /patients is the 404 page, and GET /api/patients, the red-flag
  // routes, GET /api/patients/:patientId, the Client-keyed Quick Note routes
  // under it and GET|PUT /api/patients/:patientId/quick-facts and /goals
  // answer 404. Targeting is human-owned.
  patientDetail: "patient-detail",
  // NO-861: the Appointment Actual Duration routes under /api/appointments.
  // Default OFF; the routes answer 404 until the flag is on for the signed-in
  // user. NO-862 creates the PostHog flag and the UI.
  sessionTimeCalculator: "session-time-calculator",
  // RD Schedule Manager Project flag (P-NO-107). Default OFF; targeting is
  // human-owned. NO-869: the RD Home agenda draws each missed status
  // (cancelled, late cancel, no-show, rescheduled) in its own status pair;
  // while off the agenda keeps today's statuses. NO-867: GET|PUT
  // /api/rd-hours and GET|PUT /api/rd-hours/weeks/:weekStart (an RD's
  // bookable hours and breaks in NDD) answer 404 before any read or write
  // while it is off. NO-868: GET /api/rd-schedule/week, one RD's week with
  // status tags and weekly totals, answers 404 before anything is read or
  // recorded while it is off. NO-871: GET|POST /api/rd-schedule/blocks and
  // DELETE /api/rd-schedule/blocks/:id (an RD's blocked time: breaks, project
  // time and personal time) answer 404 before any read or write while it is
  // off, unset or unevaluable. NO-872: GET /api/rd-schedule/open-times,
  // /preview, /appointment-types, /clients, /appointments/:appointmentId/options
  // and /actions/:actionId, and POST /api/rd-schedule/actions and
  // /actions/:actionId/retry answer 404 before anything is read, recorded or
  // enqueued while it is off. NO-875: the appointment details panel renders
  // nothing while it is off. Later Project Tickets add the Week and
  // Availability views under the same key (NO-874, NO-878).
  rdSchedule: "rd-schedule",
  // Patient Messaging Project flag (P-NO-108), checked per RD. NO-925: the
  // Messages rail row, the Messages pages (/messages, /messages/$patientId),
  // the Messages link on the patient page (which also needs patient-detail)
  // and GET /api/messages/threads[/:patientId]. Default OFF; while off, or
  // when the flag cannot be read, none of those exist and the routes answer
  // 404 before anything is read or recorded. Targeting is human-owned.
  patientMessaging: "patient-messaging",
} as const;

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

export const RD_HOME_FLAG = FEATURE_FLAGS.rdHome;
export const SESSION_PATIENT_LINKS_FLAG = FEATURE_FLAGS.sessionPatientLinks;
export const SOCIAL_NOTES_FLAG = FEATURE_FLAGS.socialNotes;
export const SOAP_ENHANCEMENT_FLAG = FEATURE_FLAGS.soapEnhancement;
export const ALWAYS_BRAND_FLAG = FEATURE_FLAGS.alwaysBrand;
export const SOAP_PROGRESS_STREAM_FLAG = FEATURE_FLAGS.soapProgressStream;
export const MANUAL_SOAP_GENERATION_FLAG = FEATURE_FLAGS.manualSoapGeneration;
export const SOAP_RE_ENHANCE_FLAG = FEATURE_FLAGS.soapReEnhance;
export const SOAP_AUTO_ENHANCE_FLAG = FEATURE_FLAGS.soapAutoEnhance;
export const NOTIFICATIONS_FLAG = FEATURE_FLAGS.notifications;
export const PATIENT_DETAIL_FLAG = FEATURE_FLAGS.patientDetail;
export const RD_SCHEDULE_FLAG = FEATURE_FLAGS.rdSchedule;
export const PATIENT_MESSAGING_FLAG = FEATURE_FLAGS.patientMessaging;

export const REMOTE_CONFIGS = {
  clinicalLeadershipEmails: "clinical-leadership-emails",
} as const;

export type RemoteConfigKey = (typeof REMOTE_CONFIGS)[keyof typeof REMOTE_CONFIGS];
